import * as Sentry from "@sentry/react";

import { NameEditDialog } from "@/components/modals/NameEditDialog";
import { useUpdateTemplateCategoryMutation } from "@/modules/snapshots/businessReports.api";

interface Props {
  open: boolean;
  onClose: () => void;
  categoryId: string;
  currentName: string;
}

export const RenameCategoryModal = ({ open, onClose, categoryId, currentName }: Props) => {
  const [updateCategory, { isLoading }] = useUpdateTemplateCategoryMutation();

  const handleSubmit = async (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === currentName) return;
    await updateCategory({ id: categoryId, data: { name: trimmed } }).unwrap();
  };

  const getErrorMessage = (error: unknown) => {
    Sentry.captureException(error, {
      tags: { feature: "snapshot-categories", operation: "rename" },
      extra: { categoryId },
    });
    console.error("Failed to rename category", error);
    const status = (error as { status?: number })?.status;
    const detail = (error as { data?: { detail?: string } })?.data?.detail;
    if (status === 409) return detail ?? "A category with this name already exists.";
    return detail ?? "Could not rename category. Please try again.";
  };

  return (
    <NameEditDialog
      open={open}
      onClose={onClose}
      title="Rename category"
      placeholder="Category name"
      currentName={currentName}
      isLoading={isLoading}
      autoFocus
      loadingText="Saving..."
      onSubmit={handleSubmit}
      getErrorMessage={getErrorMessage}
    />
  );
};
